import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, Layers, Plus, Target } from 'lucide-react';
import { ASSET_CLASS_BY_ID } from '@/constants/asset-classes';
import { Layout } from '@/components/Layout';
import { useI18n } from '@/i18n/use-i18n';
import {
  portfolioService,
  targetAssetsService,
  type TargetPortfolioResponse,
} from '@/services';
import { AssetClassCode, FlatAsset } from '@/types/assets';
import { AssetCard } from './components/AssetCard';
import { AddAssetDialog } from './components/AddAssetDialog';
import { EditAssetDialog, type EditAssetData } from './components/EditAssetDialog';
import './target-portfolio.css';

type ViewMode = 'cards' | 'grouped';

export function TargetPortfolio() {
  const { strings } = useI18n();
  const queryClient = useQueryClient();
  const assetClassCopy = strings.assetClassesCatalog;
  const hasMarkedConsolidation = useRef(false);

  const targetPortfolioQueryKey = ['target-portfolio'];
  const {
    data: targetPortfolioData,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery<TargetPortfolioResponse>({
    queryKey: targetPortfolioQueryKey,
    queryFn: targetAssetsService.getTargetPortfolio,
  });

  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<EditAssetData | null>(null);
  const [actionError, setActionError] = useState('');

  // Build class info map from API data
  const classInfoMap = useMemo(() => {
    if (!targetPortfolioData?.classes) return new Map<string, { code: AssetClassCode; name: string; color: string }>();
    const map = new Map<string, { code: AssetClassCode; name: string; color: string }>();
    for (const cls of targetPortfolioData.classes) {
      const code = cls.code as AssetClassCode;
      const definition = ASSET_CLASS_BY_ID[code];
      const copy = assetClassCopy[code];
      map.set(cls.id, {
        code,
        name: copy?.name ?? definition?.name ?? code.toUpperCase(),
        color: definition?.color ?? 'var(--color-brand)',
      });
    }
    return map;
  }, [targetPortfolioData, assetClassCopy]);

  // Flatten all assets for the cards view
  const flatAssets = useMemo<FlatAsset[]>(() => {
    if (!targetPortfolioData?.classes) return [];

    const assets: FlatAsset[] = [];
    for (const cls of targetPortfolioData.classes) {
      const info = classInfoMap.get(cls.id);
      if (!info) continue;

      for (const asset of cls.assets) {
        assets.push({
          id: asset.id,
          ticker: asset.ticker,
          rating: asset.rating,
          quantity: asset.quantity,
          averagePrice: asset.averagePrice ?? 0,
          logoUrl: asset.logoUrl,
          price: asset.price,
          shortName: asset.shortName,
          classId: cls.id,
          classCode: info.code,
          className: info.name,
          classColor: info.color,
          amountInvested: asset.amountInvested,
          amountInvestedIsEstimated: asset.amountInvestedIsEstimated ?? false,
          currentValue: asset.currentValue,
        });
      }
    }

    return assets.sort((a, b) => a.ticker.localeCompare(b.ticker));
  }, [targetPortfolioData, classInfoMap]);

  // Group assets by class for grouped view
  const groupedAssets = useMemo(() => {
    if (!targetPortfolioData?.classes) return [];

    return targetPortfolioData.classes
      .map((cls) => {
        const info = classInfoMap.get(cls.id);
        if (!info) return null;
        return {
          id: cls.id,
          ...info,
          percentage: cls.percentage,
          assets: cls.assets
            .map((asset) => ({
              id: asset.id,
              ticker: asset.ticker,
              rating: asset.rating,
              quantity: asset.quantity,
              averagePrice: asset.averagePrice ?? 0,
              logoUrl: asset.logoUrl,
              price: asset.price,
              shortName: asset.shortName,
              classId: cls.id,
              classCode: info.code,
              className: info.name,
              classColor: info.color,
              amountInvested: asset.amountInvested,
              amountInvestedIsEstimated: asset.amountInvestedIsEstimated ?? false,
              currentValue: asset.currentValue,
            }))
            .sort((a, b) => a.ticker.localeCompare(b.ticker)),
        };
      })
      .filter(Boolean) as Array<{
        id: string;
        code: AssetClassCode;
        name: string;
        color: string;
        percentage: number;
        assets: FlatAsset[];
      }>;
  }, [targetPortfolioData, classInfoMap]);

  // Dialog class options
  const classOptions = useMemo(() => {
    if (!targetPortfolioData?.classes) return [];
    return targetPortfolioData.classes.map((cls) => {
      const info = classInfoMap.get(cls.id);
      return {
        id: cls.id,
        code: cls.code as AssetClassCode,
        name: info?.name ?? cls.code,
      };
    });
  }, [targetPortfolioData, classInfoMap]);

  // Mutations
  const markNeedsConsolidationMutation = useMutation({
    mutationFn: () => portfolioService.markNeedsConsolidation(),
    onSuccess: () => {
      hasMarkedConsolidation.current = true;
    },
  });

  const ensureNeedsConsolidation = () => {
    if (hasMarkedConsolidation.current || markNeedsConsolidationMutation.isPending) return;
    markNeedsConsolidationMutation.mutate();
  };

  const createAssetMutation = useMutation({
    mutationFn: targetAssetsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: targetPortfolioQueryKey });
      setIsDialogOpen(false);
      ensureNeedsConsolidation();
    },
    onError: (mutationError) => {
      setActionError(
        mutationError instanceof Error ? mutationError.message : strings.targetPortfolio.errors.add,
      );
    },
  });

  const removeAssetMutation = useMutation({
    mutationFn: targetAssetsService.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: targetPortfolioQueryKey });
    },
    onError: (mutationError) => {
      setActionError(
        mutationError instanceof Error ? mutationError.message : strings.targetPortfolio.errors.remove,
      );
    },
  });

  const updateAssetMutation = useMutation({
    mutationFn: targetAssetsService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: targetPortfolioQueryKey });
      setEditingAsset(null);
      ensureNeedsConsolidation();
    },
    onError: (mutationError) => {
      setActionError(
        mutationError instanceof Error ? mutationError.message : strings.targetPortfolio.errors.update,
      );
    },
  });

  const handleAddAsset = (data: {
    assetClassId: string;
    ticker: string;
    quantity: number;
    rating: number;
    averagePrice: number;
  }) => {
    setActionError('');
    createAssetMutation.mutate(data);
  };

  const handleEditAsset = (asset: FlatAsset) => {
    setEditingAsset({
      id: asset.id,
      ticker: asset.ticker,
      quantity: asset.quantity,
      rating: asset.rating,
      averagePrice: asset.averagePrice,
    });
  };

  const handleUpdateAsset = (data: { id: string; ticker: string; quantity: number; rating: number; averagePrice: number }) => {
    setActionError('');
    updateAssetMutation.mutate(data);
  };

  const handleRemoveAsset = (assetId: string) => {
    setActionError('');
    queryClient.setQueryData<TargetPortfolioResponse | undefined>(targetPortfolioQueryKey, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        classes: prev.classes.map((cls) => ({
          ...cls,
          assets: cls.assets.filter((asset) => asset.id !== assetId),
        })),
      };
    });
    removeAssetMutation.mutate(assetId);
  };

  const errorMessage = isError
    ? error instanceof Error
      ? error.message
      : strings.targetPortfolio.errors.loadClasses
    : '';

  const isLoadingData = isLoading || isFetching;
  const hasClasses = (targetPortfolioData?.classes?.length ?? 0) > 0;
  const hasAssets = flatAssets.length > 0;

  return (
    <Layout>
      <section className="target app-page">
        <header className="target__header">
          <div className="target__titles">
            <p className="target__eyebrow">{strings.targetPortfolio.header.eyebrow}</p>
            <h1>{strings.targetPortfolio.header.title}</h1>
            <span>{strings.targetPortfolio.header.subtitle}</span>
          </div>
          {hasAssets && (
            <div className="target__toolbar">
              <div className="target__view-toggle" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'cards'}
                  className={`target__view-btn${viewMode === 'cards' ? ' is-active' : ''}`}
                  onClick={() => setViewMode('cards')}
                >
                  <LayoutGrid size={16} />
                  {strings.targetPortfolio.viewMode.cards}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'grouped'}
                  className={`target__view-btn${viewMode === 'grouped' ? ' is-active' : ''}`}
                  onClick={() => setViewMode('grouped')}
                >
                  <Layers size={16} />
                  {strings.targetPortfolio.viewMode.grouped}
                </button>
              </div>
              <button
                type="button"
                className="target__add-btn"
                onClick={() => setIsDialogOpen(true)}
              >
                <Plus size={16} />
                {strings.targetPortfolio.addAsset.button}
              </button>
            </div>
          )}
        </header>

        {actionError && (
          <div className="target__feedback target__feedback--error" role="alert">
            {actionError}
          </div>
        )}

        {isLoadingData ? (
          <div className="target__skeleton-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="target__skeleton-card">
                <div className="target__skel-row">
                  <div className="target__skel target__skel--avatar" />
                  <div className="target__skel target__skel--title" />
                  <div className="target__skel target__skel--badge" />
                </div>
                <div className="target__skel target__skel--name" />
                <div className="target__skel-body">
                  <div className="target__skel target__skel--field" />
                  <div className="target__skel target__skel--field" />
                  <div className="target__skel target__skel--field" />
                  <div className="target__skel target__skel--field" />
                </div>
              </div>
            ))}
          </div>
        ) : errorMessage ? (
          <div className="target__empty-state">
            <span className="target__empty-icon"><Target size={28} /></span>
            <strong>{errorMessage}</strong>
          </div>
        ) : !hasClasses ? (
          <div className="target__empty-state">
            <span className="target__empty-icon"><Target size={28} /></span>
            <strong>{strings.targetPortfolio.empty.title}</strong>
            <p>{strings.targetPortfolio.empty.description}</p>
            <Link className="target__cta-button" to="/asset-classes">
              {strings.targetPortfolio.empty.configure}
            </Link>
          </div>
        ) : !hasAssets ? (
          <div className="target__empty-state">
            <span className="target__empty-icon"><Target size={28} /></span>
            <strong>{strings.targetPortfolio.empty.title}</strong>
            <p>{strings.targetPortfolio.empty.description}</p>
            <button
              type="button"
              className="target__cta-button"
              onClick={() => setIsDialogOpen(true)}
            >
              <Plus size={15} />
              {strings.targetPortfolio.addAsset.button}
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="target__cards-grid">
            {flatAssets.map((asset) => (
              <AssetCard
                key={asset.id}
                assetData={asset}
                onEdit={() => handleEditAsset(asset)}
                onDelete={() => handleRemoveAsset(asset.id)}
              />
            ))}
          </div>
        ) : (
          <div className="target__grouped">
            {groupedAssets.map((group) => (
              <div
                key={group.id}
                className="target__group"
                style={{ '--accent-color': group.color } as CSSProperties}
              >
                <div className="target__group-header">
                  <div className="target__group-info">
                    <span
                      className="target__group-badge"
                      style={{ '--group-color': group.color } as CSSProperties}
                    >
                      {group.name}
                    </span>
                    <span className="target__group-count">
                      {group.assets.length}{' '}
                      {group.assets.length === 1 ? 'ativo' : 'ativos'}
                    </span>
                  </div>
                  <span className="target__group-pct">
                    {group.percentage}%
                  </span>
                </div>
                {group.assets.length === 0 ? (
                  <div className="target__group-empty">
                    Nenhum ativo nesta classe.
                  </div>
                ) : (
                  <div className="target__cards-grid target__cards-grid--grouped">
                    {group.assets.map((asset) => (
                      <AssetCard
                        key={asset.id}
                        assetData={asset}
                        onEdit={() => handleEditAsset(asset)}
                        onDelete={() => handleRemoveAsset(asset.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <AddAssetDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          classes={classOptions}
          onSubmit={handleAddAsset}
          isSubmitting={createAssetMutation.isPending}
        />

        <EditAssetDialog
          open={editingAsset !== null}
          onOpenChange={(open) => { if (!open) setEditingAsset(null); }}
          asset={editingAsset}
          onSubmit={handleUpdateAsset}
          isSubmitting={updateAssetMutation.isPending}
        />
      </section>
    </Layout>
  );
}
